import { getMaxConcurrentStreamsConfig } from "../../lib/runtimeConfig";

// Concurrent-stream budget, shared across every player tile on the page.
//
// Deliberately a plain closure store, not React state: a tile asking for a
// slot mid-render must not trip the "setState during render" rule, and a
// framework-free store is trivially unit-testable without mounting anything.
//
// Policy: refuse when full, and never evict. Silently dropping someone's live
// view to make room for a new one is a worse surprise than being told to wait.
//
// The store itself holds no queue — a refused caller is simply told no. What
// waits is the caller: useHlsStream puts a refused tile into its "queued"
// state and subscribes here, so it starts as soon as another tile releases.
// That is not eviction, and it is what the store's own subscribe() is for;
// before it was wired up, a refused tile stayed dead until the page reloaded.
export interface StreamSlots {
  /** Idempotent for a tile that already holds a slot. */
  acquire: (tileId: string) => boolean;
  release: (tileId: string) => void;
  holds: (tileId: string) => boolean;
  active: () => number;
  limit: number;
  subscribe: (listener: () => void) => () => void;
}

export function createStreamSlots(limit: number): StreamSlots {
  const held = new Set<string>();
  const listeners = new Set<() => void>();

  const notify = () => {
    for (const l of listeners) l();
  };

  return {
    limit,
    acquire(tileId) {
      if (held.has(tileId)) return true; // re-acquire is not a second claim
      if (held.size >= limit) return false;
      held.add(tileId);
      notify();
      return true;
    },
    release(tileId) {
      if (held.delete(tileId)) notify();
    },
    holds(tileId) {
      return held.has(tileId);
    },
    active() {
      return held.size;
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

/** No cap at all — used for single-tile surfaces (a detail view, a modal). */
export function createUnlimitedSlots(): StreamSlots {
  return createStreamSlots(Number.POSITIVE_INFINITY);
}

export const DEFAULT_MAX_CONCURRENT_STREAMS = 9;

export function getMaxConcurrentStreams(): number {
  const raw = getMaxConcurrentStreamsConfig();
  const parsed = Number.parseInt(raw ?? "", 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return DEFAULT_MAX_CONCURRENT_STREAMS;
  return parsed;
}
